import React, { type ReactElement } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography
} from '@mui/material'
import {
  Form,
  Loading,
  required,
  Title,
  useGetOne,
  useNotify,
  useTranslate
} from 'react-admin'
import { FileInput } from 'components/Util'
import { useFormContext, useWatch } from 'react-hook-form'
import log from 'loglevel'
import { doAnnouncesDownload, doAnnouncesUpload } from './functions'
import { json2xlsx } from '@mzc-watchover/json2xlsx'
import { compact, concat, find, isEmpty, isNil, map, sortBy } from 'lodash/fp'
import { size } from 'lodash'
import { type AnnounceItem } from '../../../../cdk/bapp/lib/lambda-api/src/types'
import * as xlsx from 'xlsx'
import { drawAnnounces } from 'components/Announce'
import { isAfter, parseISO } from 'date-fns/fp'
import { type Partner } from '@mzc-watchover/adminapp-appsync'

const AdminDashboardFormItem: React.FC<{
  partner: string
  partnerName?: string
  common?: boolean
}> = ({ partner, partnerName, common }) => {
  const notifiy = useNotify()
  const translate = useTranslate()
  const { setValue } = useFormContext()
  const announces = useWatch({
    name: !isNil(common) && common ? 'common.announces' : 'announces'
  })
  const downloadFiles = useWatch({
    name: !isNil(common) && common ? 'common.downloadFiles' : 'downloadFiles'
  })

  const [disabled, setDisabled] = React.useState(true)
  const prefix = !isNil(common) && common ? 'common.' : ''
  const onClickDownload = (): void => {
    doAnnouncesDownload(partner, common)
      .then(async ({ data, files }) => {
        if (isNil(data)) {
          notifiy('resources.admin.annnounces.downloadFail')
        }
        const xlsx = await json2xlsx(
          isEmpty(data)
            ? [
                {
                  date: '2025-07-23',
                  content: 'サンプル',
                  limit: '2025-07-01',
                  file: ''
                }
              ]
            : data,
          undefined,
          {
            headers: {
              date: '日付',
              content: '内容',
              limit: '掲載期限',
              file: 'ダウンロード可能にするファイル'
            },
            validations: isNil(files)
              ? []
              : [
                  {
                    column: 4,
                    endRow: size(data) + 100,
                    title: 'ダウンロード可能にするファイル',
                    list: files,
                    allowBlank: true
                  }
                ]
          }
        )
        if (!isNil(xlsx)) {
          const blob = new Blob([xlsx], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = URL.createObjectURL(blob)
          const download = document.createElement('a')
          download.href = url
          download.download = 'お知らせ一覧.xlsx'
          download.click()
          window.URL.revokeObjectURL(url)
        }
      })
      .catch((error) => {
        log.error('[AdminDashboard.onClick]', {
          error
        })
      })
  }
  const onClickUpload = (): void => {
    // const { announces, downloadFiles } =
    //   !isNil(common) && common ? getValues()['common'] : getValues()
    const data = new FormData()
    if (!isNil(announces)) data.append('announces', announces)
    for (let i = 0; i < size(downloadFiles); i++) {
      if (!isNil(downloadFiles[i])) {
        data.append(
          'downloadFiles',
          downloadFiles[i],
          encodeURIComponent(downloadFiles[i].name)
        )
      }
    }
    if (!data.has('announces') && !data.has('downloadFiles')) return
    doAnnouncesUpload(partner, data, common)
      .then((status) => {
        if (status !== 202) {
          notifiy('resources.admin.announces.uploadFail')
        } else {
          notifiy('resources.admin.announces.uploadComplete')
          if (!isNil(common) && common) {
            setValue('common.announces', undefined)
            setValue('common.downloadFiles.0', undefined, { shouldDirty: true })
            setValue('common.downloadFiles.1', undefined, { shouldDirty: true })
            setValue('common.downloadFiles.2', undefined, { shouldDirty: true })
            setValue('common.downloadFiles.3', undefined, { shouldDirty: true })
            setValue('common.downloadFiles.4', undefined, { shouldDirty: true })
          } else {
            setValue('announces', undefined)
            setValue('downloadFiles.0', undefined, { shouldDirty: true })
            setValue('downloadFiles.1', undefined, { shouldDirty: true })
            setValue('downloadFiles.2', undefined, { shouldDirty: true })
            setValue('downloadFiles.3', undefined, { shouldDirty: true })
            setValue('downloadFiles.4', undefined, { shouldDirty: true })
          }
        }
      })
      .catch((error) => {
        log.error('[AdminDashboard.onClick]', {
          error
        })
        notifiy('resources.admin.annnounces.uploadFail')
      })
  }
  React.useEffect(() => {
    setDisabled(
      isNil(announces) &&
        isNil(find((item: any) => !isNil(item))(downloadFiles))
    )
  }, [announces, downloadFiles])
  return (
    <Grid container alignItems="center">
      <Grid item xs={12} sx={{ mb: '0.5em' }}>
        <Typography variant="h5">
          {`${translate(
            isNil(common) || !common
              ? 'resources.admin.announces.title'
              : 'resources.admin.announces.commonTitle'
          )}${!isNil(partnerName) ? '(' + partnerName + '向け)' : ''}`}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="h6">
          {translate('resources.admin.announces.files.label')}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <FileInput
          source={`${prefix}downloadFiles.0`}
          placeholder={translate('resources.admin.announces.files.fileSelect')}
          inputProps={{
            accept: 'application/pdf'
          }}
          variant="outlined"
          validate={required()}
        />
      </Grid>
      <Grid item xs={3} />
      <Grid item xs={9}>
        <FileInput
          source={`${prefix}downloadFiles.1`}
          placeholder={translate('resources.admin.announces.files.fileSelect')}
          inputProps={{
            accept: 'application/pdf'
          }}
          variant="outlined"
          validate={required()}
        />
      </Grid>
      <Grid item xs={3} />
      <Grid item xs={9}>
        <FileInput
          source={`${prefix}downloadFiles.2`}
          placeholder={translate('resources.admin.announces.files.fileSelect')}
          inputProps={{
            accept: 'application/pdf'
          }}
          variant="outlined"
          validate={required()}
        />
      </Grid>
      <Grid item xs={3} />
      <Grid item xs={9}>
        <FileInput
          source={`${prefix}downloadFiles.3`}
          placeholder={translate('resources.admin.announces.files.fileSelect')}
          inputProps={{
            accept: 'application/pdf'
          }}
          variant="outlined"
          validate={required()}
        />
      </Grid>
      <Grid item xs={3} />
      <Grid item xs={9}>
        <FileInput
          source={`${prefix}downloadFiles.4`}
          placeholder={translate('resources.admin.announces.files.fileSelect')}
          inputProps={{
            accept: 'application/pdf'
          }}
          variant="outlined"
          validate={required()}
        />
      </Grid>
      <Grid item xs={3} sx={{ mt: '1em' }}>
        <Typography variant="h6">
          {translate('resources.admin.announces.label')}
        </Typography>
      </Grid>
      <Grid item xs={9} sx={{ mt: '1em' }}>
        <Button variant="contained" onClick={onClickDownload}>
          {translate('resources.admin.announces.download')}
        </Button>
      </Grid>
      <Grid item xs={3} />
      <Grid item xs={9}>
        <FileInput
          source={`${prefix}announces`}
          placeholder={translate('resources.admin.announces.fileSelect')}
          inputProps={{
            accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }}
          variant="outlined"
          validate={required()}
        />
      </Grid>
      <Grid item xs={10} sx={{ mt: '2em' }}>
        <Button variant="contained" onClick={onClickUpload} disabled={disabled}>
          {translate('resources.admin.announces.upload')}
        </Button>
      </Grid>
    </Grid>
  )
}
const AdminDashboardForm: React.FC<{
  partner: string
  partnerName: string
}> = ({ partner, partnerName }) => {
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const [items, setItems] = React.useState<ReactElement[]>([])
  const [open, setOpen] = React.useState(false)
  const getData = async (
    file: File,
    common: boolean
  ): Promise<AnnounceItem[]> => {
    const book = xlsx.read(await file.arrayBuffer(), {
      cellDates: true
    })
    const sheet = book.Sheets[book.SheetNames[0]]
    if (isNil(sheet)) return []
    const data = await Promise.all(
      map(async (item: AnnounceItem) => {
        const { date, content, limit, file } = item
        log.debug({ date, content, limit, file })
        return {
          date,
          content,
          limit: isEmpty(limit) ? undefined : limit,
          file: isEmpty(file) ? undefined : file,
          partner: common ? undefined : partner
        }
      })(
        xlsx.utils.sheet_to_json(sheet, {
          range: 1,
          header: ['date', 'content', 'limit', 'file'],
          raw: true,
          blankrows: false,
          defval: null
        })
      )
    )
    return data
  }
  const getAnnnounceData = async (
    announces: File | undefined,
    common: File | undefined
  ): Promise<AnnounceItem[]> => {
    let data: AnnounceItem[]
    if (!isNil(announces)) {
      data = await getData(announces, false)
    } else {
      const { data: downloadData } = await doAnnouncesDownload(partner, false)
      data = downloadData ?? []
    }
    let commonData: AnnounceItem[]
    if (!isNil(common)) {
      commonData = await getData(common, true)
    } else {
      const { data: downloadData } = await doAnnouncesDownload(partner, true)
      commonData = downloadData ?? []
    }
    return compact(
      map((item: AnnounceItem) => {
        const { date, content, limit, file } = item
        log.debug({ date, content, limit, file })
        return !isEmpty(limit) &&
          !isNil(limit) &&
          isAfter(parseISO(limit))(Date.now())
          ? undefined
          : {
              date,
              content,
              file: isEmpty(file) ? undefined : file
            }
      })(sortBy<AnnounceItem>('date')(concat(data)(commonData)))
    )
  }
  const onClick = (): void => {
    const { announces, common } = getValues()
    getAnnnounceData(announces, common.announces)
      .then((data) => {
        setItems(drawAnnounces(data))
        setOpen(true)
      })
      .catch(log.error)
  }
  const handleClose = (): void => {
    setOpen(false)
  }
  return (
    <>
      <AdminDashboardFormItem partner={partner} partnerName={partnerName} />
      <Box sx={{ mt: '3em' }}>
        <AdminDashboardFormItem partner={partner} common={true} />
      </Box>
      <Box sx={{ mt: '3em' }}>
        <Button variant="contained" onClick={onClick}>
          {translate('resources.admin.announces.preview')}
        </Button>
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Card>{items}</Card>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleClose}>
            {translate('resources.admin.announces.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const AdminDashboard: React.FC<{ partner: string }> = ({ partner }) => {
  const [partnerName, setPartnerName] = React.useState<string>('')
  const { data, isLoading } = useGetOne<{ id: string } & Partial<Partner>>(
    'partners',
    { id: partner }
  )
  React.useEffect(() => {
    if (isLoading) return
    if (!isNil(data)) {
      const { name } = data
      setPartnerName(name as string)
    }
  })
  if (isLoading) return <Loading />
  return (
    <Grid container justifyContent="center">
      <Title
        title={`管理者モード${
          !isEmpty(partnerName) ? '(' + partnerName + '向け)' : ''
        }`}
      />
      <Card
        raised
        sx={{
          width: '1280px',
          marginTop: '1em',
          marginBottom: '1em',
          backgroundColor: 'background'
        }}>
        <CardContent>
          <Form>
            <AdminDashboardForm partner={partner} partnerName={partnerName} />
          </Form>
        </CardContent>
      </Card>
    </Grid>
  )
}
